import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, BookOpen, Settings, LogOut, ListChecks, Store, CreditCard,
  ShieldCheck, ClipboardList, Menu, X, ScanSearch, Compass, Home, Columns3, Gift, Users, ReceiptText, FileText, Undo2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { QuotaBadge } from "@/components/QuotaBadge";
import { VerifyBanner } from "@/components/VerifyBanner";
import { BuyerTrnBanner } from "@/components/BuyerTrnBanner";
import { CountryPrompt } from "@/components/CountryPrompt";

const vendorNav = [
  { to: "/for-vendors", label: "For Vendors", icon: Home, testid: "nav-for-vendors" },
  { to: "/dashboard", label: "Quote Builder", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/tracker", label: "RFQ Tracker", icon: ListChecks, testid: "nav-tracker" },
  { to: "/catalogue", label: "Catalogue", icon: BookOpen, testid: "nav-catalogue" },
  { to: "/einvoicing/app", label: "Matiqs Qonnect", icon: ReceiptText, testid: "nav-einvoices" },
  { to: "/qreview", label: "QReview", icon: ScanSearch, testid: "nav-qreview" },
  { to: "/settings", label: "Vendor Profile", icon: Settings, testid: "nav-settings" },
  { to: "/referrals", label: "Refer & Earn", icon: Gift, testid: "nav-referrals" },
  { to: "/billing", label: "Plan & Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/how-it-works", label: "How it works", icon: Compass, testid: "nav-how-it-works" },
];

const contractorNav = [
  { to: "/marketplace", label: "Vendor Marketplace", icon: Store, testid: "nav-marketplace" },
  { to: "/rfqs", label: "My RFQs", icon: ClipboardList, testid: "nav-rfqs" },
  { to: "/qompare", label: "Qompare", icon: Columns3, testid: "nav-qompare" },
  { to: "/my-vendors", label: "My Vendors", icon: Users, testid: "nav-my-vendors" },
  { to: "/einvoicing/app", label: "Matiqs Qonnect", icon: ReceiptText, testid: "nav-einvoices-contractor" },
  { to: "/einvoicing/clearance", label: "ASP clearance", icon: ShieldCheck, testid: "nav-clearance-contractor" },
  { to: "/qreview", label: "QReview", icon: ScanSearch, testid: "nav-qreview" },
  { to: "/profile", label: "Company Profile", icon: Settings, testid: "nav-profile" },
  { to: "/referrals", label: "Refer & Earn", icon: Gift, testid: "nav-referrals" },
  { to: "/billing", label: "Plan & Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/how-it-works", label: "How it works", icon: Compass, testid: "nav-how-it-works" },
];

const einvoiceNav = [
  { to: "/einvoicing/app", label: "Matiqs Qonnect", icon: ReceiptText, testid: "nav-einvoices-ei", end: true },
  { to: "/einvoicing/standard", label: "Standard invoices", icon: FileText, testid: "nav-standard-ei" },
  { to: "/einvoicing/credit-notes", label: "Credit notes", icon: Undo2, testid: "nav-creditnotes-ei" },
  { to: "/einvoicing/clearance", label: "ASP clearance", icon: ShieldCheck, testid: "nav-clearance-ei" },
  { to: "/qreview", label: "QReview", icon: ScanSearch, testid: "nav-qreview" },
  { to: "/settings", label: "Company Profile", icon: Settings, testid: "nav-settings" },
  { to: "/referrals", label: "Refer & Earn", icon: Gift, testid: "nav-referrals" },
  { to: "/how-it-works", label: "How it works", icon: Compass, testid: "nav-how-it-works" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isContractor = user?.account_type === "contractor";
  const isEinvoice = user?.account_type === "einvoice";
  const base = isEinvoice ? einvoiceNav : isContractor ? contractorNav : vendorNav;
  const nav = user?.is_admin
    ? [...base, { to: "/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" }]
    : base;
  const home = isEinvoice ? "/einvoicing/app" : isContractor ? "/marketplace" : "/dashboard";

  const sidebarNav = (prefix = "") => (
    <div className="flex flex-col h-full">
      <button
        data-testid={`${prefix}sidebar-brand`}
        onClick={() => { navigate(home); setOpen(false); }}
        className="flex items-center h-16 px-5 shrink-0 border-b border-border"
      >
        <img src="/matiqs-logo.png" alt="Matiqs" className="brand-logo h-7 w-auto" />
      </button>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`${prefix}${n.testid}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary/10 text-primary font-semibold"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`
            }
          >
            <n.icon className="w-4 h-4 shrink-0" />
            <span className="truncate">{n.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-border space-y-3 shrink-0">
        {!prefix && !isEinvoice && <QuotaBadge />}
        <div className="px-2">
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
            {user?.company_name}
          </div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
        </div>
        <button
          data-testid={`${prefix}logout-button`}
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
        >
          <LogOut className="w-4 h-4 shrink-0" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:flex print:hidden fixed inset-y-0 left-0 w-60 flex-col bg-card border-r border-border z-30">
        {sidebarNav()}
      </aside>

      <header className="lg:hidden print:hidden sticky top-0 z-30 h-16 bg-card/90 backdrop-blur-md border-b border-border flex items-center justify-between px-4">
        <button data-testid="sidebar-toggle" onClick={() => setOpen(true)} className="w-10 h-10 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground">
          <Menu className="w-5 h-5" />
        </button>
        <img src="/matiqs-logo.png" alt="Matiqs" className="brand-logo h-7 w-auto" />
        <div className="w-10" />
      </header>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-64 bg-card border-r border-border">
            <button data-testid="sidebar-close" onClick={() => setOpen(false)} className="absolute right-2 top-4 w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground">
              <X className="w-4 h-4" />
            </button>
            {sidebarNav("m-")}
          </div>
        </div>
      )}

      <main className="lg:pl-60 print:pl-0">
        <div className="max-w-7xl mx-auto px-5 py-8 lg:py-10 print:max-w-none print:mx-0 print:px-0 print:py-0">
          <div className="print:hidden">
            <VerifyBanner />
            <BuyerTrnBanner />
          </div>
          {children}
        </div>
      </main>
      <CountryPrompt />
    </div>
  );
}
